/** Modern login screen mirroring the suite's two-column auth layout; the auth endpoint is unchanged. */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle, Eye, EyeOff, Loader2, Mail, Lock, Sparkles,
  ChefHat, MapPin,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const buildSaasBoxxLoginUrl = () => {
  // Explicit override via build env wins.
  const explicit = process.env.REACT_APP_SAASBOXX_LOGIN_URL;
  if (explicit) {
    const sep = explicit.includes('?') ? '&' : '?';
    return `${explicit}${sep}return_to=restoboxx`;
  }
  const host = window.location.hostname;
  const labels = host.split('.');
  const isIp = /^\d+\.\d+\.\d+\.\d+$/.test(host);
  const isLocal = host === 'localhost' || host.endsWith('.localhost');
  const looksLikeTenant =
    !isIp && !isLocal && labels.length > 2 && labels[0] !== 'www';
  const tenant = looksLikeTenant ? labels[0] : '';
  const base = tenant
    ? `https://${tenant}.saas-boxx.com/login`
    : 'https://saas-boxx.com/login';
  return `${base}?return_to=restoboxx`;
};

const ModernLogin = ({ onLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Break-glass recovery accounts can require a 2FA code; the field appears only once the backend asks.
  const [require2fa, setRequire2fa] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  // Auth payload held back while the user picks among multiple locations.
  const [pendingAuth, setPendingAuth] = useState(null);

  // Persist the session + chosen location, then hand off to the app.
  const finalizeLogin = async (data, location) => {
    localStorage.setItem('sessionToken', data.data.token);
    localStorage.setItem('userData', JSON.stringify(data.data.user));
    const restaurantId = data.data.restaurant?.id || data.data.user?.restaurant_id || data.data.user?.restaurant || null;
    if (restaurantId) {
      localStorage.setItem('restaurantId', String(restaurantId));
      localStorage.setItem('restaurant', JSON.stringify(data.data.restaurant || { id: restaurantId }));
    }
    if (location) {
      localStorage.setItem('locationId', String(location.id));
      localStorage.setItem('location', JSON.stringify(location));
    } else {
      localStorage.removeItem('locationId');
      localStorage.removeItem('location');
    }
    if (remember) localStorage.setItem('rememberMe', '1');
    // Best-effort session start, as the previous staff login did
    try {
      await fetch(`${API_URL}/users/session/start/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${data.data.token}`,
        },
        body: JSON.stringify({ user_id: data.data.user.id }),
      });
    } catch (e) { /* non-fatal */ }
    if (typeof onLogin === 'function') {
      onLogin(data.data.user, { token: data.data.token, restaurantId, locationId: location?.id || null });
    }
    navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) return setError('Email is required');
    if (!password) return setError('Password is required');

    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/users/login/auth/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, ...(require2fa ? { totp_code: totpCode } : {}) }),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        const locations = Array.isArray(data.data.locations) ? data.data.locations : [];
        // More than one location → let the user choose before entering.
        if (locations.length > 1) {
          setPendingAuth(data);
          setLoading(false);
          return;
        }
        // Zero or one location → auto-select and go.
        await finalizeLogin(data, locations[0] || null);
      } else {
        // Break-glass account needs a 2FA code → reveal the field.
        if (data.require_2fa || data.breakglass) setRequire2fa(true);
        setError(data.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // Called from the location-picker overlay.
  const chooseLocation = async (location) => {
    if (!pendingAuth) return;
    setLoading(true);
    try {
      await finalizeLogin(pendingAuth, location);
    } finally {
      setLoading(false);
      setPendingAuth(null);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Location picker (shown when the account has >1 location) ── */}
      {pendingAuth && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="h-5 w-5 text-red-600" />
              <h2 className="text-lg font-semibold text-gray-900">Choose a location</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Your account has access to multiple locations. Pick one to continue.
            </p>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {(pendingAuth.data.locations || []).map((loc) => (
                <button
                  key={loc.id}
                  type="button"
                  disabled={loading}
                  onClick={() => chooseLocation(loc)}
                  className="w-full flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 text-left hover:border-red-400 hover:bg-red-50 transition-colors disabled:opacity-60"
                >
                  <span>
                    <span className="block font-medium text-gray-900">{loc.name}</span>
                    {loc.city && <span className="block text-xs text-gray-500">{loc.city}</span>}
                  </span>
                  {loc.is_primary && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
                      Primary
                    </span>
                  )}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setPendingAuth(null)}
              disabled={loading}
              className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Left: brand panel (hidden on mobile) ───────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-red-700 via-red-600 to-orange-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <ChefHat className="w-7 h-7" />
              </div>
              <h1 className="text-2xl font-bold">RestoBOXX</h1>
            </div>
            <h2 className="text-4xl font-bold leading-tight">
              Run your restaurant. <br />Sell more food.
            </h2>
            <p className="text-lg text-white/90">
              Orders, menu, tables, kitchen, delivery, loyalty &mdash; one screen,
              every shift.
            </p>
          </div>

          {/* Restaurant-themed illustration */}
          <div className="flex items-center justify-center flex-1 py-8">
            <div className="relative w-full max-w-lg">
              <svg viewBox="0 0 800 600" className="w-full h-auto drop-shadow-2xl">
                {/* Background circles */}
                <circle cx="150" cy="150" r="100" fill="rgba(255,255,255,0.1)" />
                <circle cx="650" cy="450" r="120" fill="rgba(255,255,255,0.1)" />

                {/* Plate */}
                <ellipse cx="400" cy="380" rx="200" ry="40" fill="#1F2937" opacity="0.6" />
                <circle cx="400" cy="340" r="160" fill="white" />
                <circle cx="400" cy="340" r="148" fill="#F9FAFB" />
                <circle cx="400" cy="340" r="120" fill="white" stroke="#E5E7EB" strokeWidth="2" />

                {/* Food on plate */}
                <ellipse cx="380" cy="320" rx="50" ry="30" fill="#DC2626" />
                <ellipse cx="380" cy="318" rx="42" ry="22" fill="#EF4444" />
                <circle cx="420" cy="345" r="20" fill="#FBBF24" />
                <circle cx="420" cy="343" r="14" fill="#FCD34D" />
                <ellipse cx="365" cy="360" rx="22" ry="14" fill="#16A34A" />
                <ellipse cx="365" cy="358" rx="16" ry="10" fill="#22C55E" />

                {/* Steam */}
                <path d="M 360 270 Q 370 250 365 230 Q 360 215 370 200"
                  stroke="white" strokeWidth="3" fill="none" opacity="0.7"
                  strokeLinecap="round" />
                <path d="M 400 265 Q 408 245 403 225 Q 398 210 408 195"
                  stroke="white" strokeWidth="3" fill="none" opacity="0.6"
                  strokeLinecap="round" />
                <path d="M 440 275 Q 448 255 443 235 Q 438 220 448 205"
                  stroke="white" strokeWidth="3" fill="none" opacity="0.7"
                  strokeLinecap="round" />

                {/* Fork left */}
                <rect x="170" y="280" width="6" height="180" rx="3" fill="white" opacity="0.9" />
                <rect x="160" y="260" width="3" height="40" rx="1" fill="white" opacity="0.9" />
                <rect x="168" y="260" width="3" height="40" rx="1" fill="white" opacity="0.9" />
                <rect x="176" y="260" width="3" height="40" rx="1" fill="white" opacity="0.9" />
                <rect x="184" y="260" width="3" height="40" rx="1" fill="white" opacity="0.9" />

                {/* Knife right */}
                <rect x="624" y="280" width="6" height="180" rx="3" fill="white" opacity="0.9" />
                <path d="M 620 260 L 634 260 L 634 300 L 627 300 Z" fill="white" opacity="0.9" />

                {/* Receipt / order ticket */}
                <rect x="100" y="80" width="140" height="180" rx="8"
                  fill="white" opacity="0.95" />
                <rect x="115" y="100" width="80" height="8" rx="2" fill="#DC2626" />
                <rect x="115" y="120" width="110" height="3" rx="1" fill="#E5E7EB" />
                <rect x="115" y="135" width="90" height="3" rx="1" fill="#E5E7EB" />
                <rect x="115" y="150" width="100" height="3" rx="1" fill="#E5E7EB" />
                <rect x="115" y="170" width="60" height="3" rx="1" fill="#E5E7EB" />
                <rect x="115" y="185" width="70" height="3" rx="1" fill="#E5E7EB" />
                <rect x="115" y="210" width="110" height="6" rx="2" fill="#DC2626" />
                <rect x="115" y="225" width="50" height="3" rx="1" fill="#E5E7EB" />
                <circle cx="220" cy="226" r="5" fill="#16A34A" />

                {/* QR floating right */}
                <rect x="600" y="120" width="100" height="100" rx="8" fill="white" />
                <rect x="615" y="135" width="20" height="20" fill="#1F2937" />
                <rect x="665" y="135" width="20" height="20" fill="#1F2937" />
                <rect x="615" y="185" width="20" height="20" fill="#1F2937" />
                <rect x="645" y="155" width="10" height="10" fill="#1F2937" />
                <rect x="660" y="170" width="10" height="10" fill="#1F2937" />
                <rect x="645" y="185" width="15" height="10" fill="#1F2937" />
                <rect x="670" y="195" width="15" height="10" fill="#1F2937" />

                {/* Chart line */}
                <path d="M 100 510 L 200 480 L 300 490 L 400 460 L 500 470 L 600 440 L 700 450"
                  stroke="white" strokeWidth="3" fill="none" opacity="0.8" />
                <circle cx="200" cy="480" r="5" fill="white" />
                <circle cx="400" cy="460" r="5" fill="white" />
                <circle cx="600" cy="440" r="5" fill="white" />
              </svg>
            </div>
          </div>

          <div className="text-sm text-white/70">
            © {new Date().getFullYear()} RestoBOXX. All rights reserved.
          </div>
        </div>
      </div>

      {/* ── Right: auth form ───────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile-only brand strip */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-600 to-orange-500 flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-gray-900">RestoBOXX</span>
          </div>

          <div className="text-center space-y-2">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Welcome back
            </h2>
            <p className="text-gray-500">
              Sign in to manage your restaurant
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                  disabled={loading}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none"
                  placeholder="you@restaurant.com"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
                  disabled={loading}
                  className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-2.5 p-1 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword
                    ? <EyeOff className="w-4 h-4" />
                    : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {require2fa && (
              <div className="space-y-2">
                <label htmlFor="totp" className="text-sm font-medium text-gray-700">
                  Two-factor code
                </label>
                <input
                  id="totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={totpCode}
                  onChange={(e) => { setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6)); if (error) setError(''); }}
                  disabled={loading}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-300 text-center text-lg tracking-[0.4em] focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none"
                  autoFocus
                />
                <p className="text-xs text-gray-500">
                  Protected account — enter the code from your authenticator app.
                </p>
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <span className="text-gray-700">Remember me</span>
              </label>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="font-medium text-red-600 hover:text-red-700"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-gradient-to-r from-red-600 to-orange-500 text-white font-medium hover:from-red-700 hover:to-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</>
                : 'Sign In'}
            </button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-400">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => { window.location.href = buildSaasBoxxLoginUrl(); }}
            className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 transition"
          >
            <Sparkles className="w-4 h-4 text-red-500" />
            Sign in with SaaSBOXX
          </button>

          <p className="text-center text-xs text-gray-400">
            Need help? Contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ModernLogin;
